import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { api } from '../services/api';

// Import request function for direct API calls
const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

async function request(path, { method = 'GET', body, token } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  
  try {
    const res = await fetch(`${BASE_URL}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });
    
    const contentType = res.headers.get('content-type') || '';
    const data = contentType.includes('application/json') ? await res.json() : await res.text();
    
    if (!res.ok) {
      throw new Error(data.message || data || res.statusText);
    }
    
    return data;
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
}

const SettingsContext = createContext();

export const useSettings = () => {
	const ctx = useContext(SettingsContext);
	if (!ctx) throw new Error('useSettings must be used within SettingsProvider');
	return ctx;
};

export const SettingsProvider = ({ children }) => {
	const { user, token, isAuthenticated } = useAuth();
	const [preferences, setPreferences] = useState({
		theme: 'light',
		currency: 'USD',
		dateFormat: 'YYYY-MM-DD'
	});
	const [categories, setCategories] = useState({
		income: ['Salary', 'Business', 'Freelance', 'Investment', 'Other Income'],
		expense: ['Food & Dining', 'Transportation', 'Shopping', 'Entertainment', 'Bills & Utilities', 'Healthcare', 'Education', 'Travel', 'Other Expense']
	});

	// Load user preferences and categories from backend
	useEffect(() => {
		if (isAuthenticated && user) {
			// Load preferences from user object
			if (user.preferences) {
				setPreferences({
					theme: user.preferences.theme || 'light',
					currency: user.currency || 'USD',
					dateFormat: user.preferences.dateFormat || 'YYYY-MM-DD',
					language: user.preferences.language || 'English',
					currencyDisplay: user.preferences.currencyDisplay || 'symbol',
					defaultTransactionType: user.preferences.defaultTransactionType || 'expense'
				});
			}
			// Load categories from user object
			if (user.categories) {
				setCategories(user.categories);
			}
		}
	}, [user, isAuthenticated]);

	const formatCurrency = useCallback((amount, options = {}) => {
		const currency = preferences.currency || user?.currency || 'USD';
		const display = preferences.currencyDisplay || 'symbol';
		const { compact = true, maxLength = 15 } = options;
		
		// Use appropriate locale for the currency
		const locale = currency === 'INR' ? 'en-IN' : 
		              currency === 'EUR' ? 'de-DE' : 
		              currency === 'GBP' ? 'en-GB' : 
		              currency === 'CAD' ? 'en-CA' : 
		              currency === 'AUD' ? 'en-AU' : 'en-US';
		
		// Helper function to format large numbers compactly
		const formatCompactNumber = (value) => {
			const absValue = Math.abs(value);
			
			// For Indian currency (INR), use Indian numbering system
			if (currency === 'INR') {
				if (absValue >= 10000000) { // 1 Crore
					return `${(value / 10000000).toFixed(1)}Cr`;
				} else if (absValue >= 100000) { // 1 Lakh
					return `${(value / 100000).toFixed(1)}L`;
				} else if (absValue >= 1000) { // 1 Thousand
					return `${(value / 1000).toFixed(1)}K`;
				}
			} else {
				// For other currencies, use standard abbreviations
				if (absValue >= 1000000000) { // 1 Billion
					return `${(value / 1000000000).toFixed(1)}B`;
				} else if (absValue >= 1000000) { // 1 Million
					return `${(value / 1000000).toFixed(1)}M`;
				} else if (absValue >= 1000) { // 1 Thousand
					return `${(value / 1000).toFixed(1)}K`;
				}
			}
			return value.toFixed(2);
		};

		// Get currency symbol
		const getCurrencySymbol = () => {
			try {
				return new Intl.NumberFormat(locale, { 
					style: 'currency', 
					currency,
					currencyDisplay: 'symbol'
				}).format(0).replace(/[\d\s.,]/g, '');
			} catch {
				return currency === 'INR' ? '₹' : '$';
			}
		};

		// Format based on display preference
		let formattedAmount;
		
		if (compact && Math.abs(amount) >= 1000) {
			const compactNumber = formatCompactNumber(amount);
			const symbol = getCurrencySymbol();
			
			if (display === 'symbol') {
				formattedAmount = `${symbol}${compactNumber}`;
			} else if (display === 'code') {
				formattedAmount = `${currency} ${compactNumber}`;
			} else if (display === 'name') {
				const currencyNames = {
					USD: 'US Dollar', EUR: 'Euro', GBP: 'British Pound', 
					INR: 'Indian Rupee', CAD: 'Canadian Dollar', AUD: 'Australian Dollar'
				};
				formattedAmount = `${compactNumber} ${currencyNames[currency] || currency}`;
			} else {
				formattedAmount = `${symbol}${compactNumber}`;
			}
		} else {
			// Use standard formatting for smaller numbers
			if (display === 'symbol') {
				formattedAmount = new Intl.NumberFormat(locale, { 
					style: 'currency', 
					currency,
					currencyDisplay: 'symbol'
				}).format(amount);
			} else if (display === 'code') {
				formattedAmount = `${currency} ${new Intl.NumberFormat(locale, { 
					minimumFractionDigits: 2,
					maximumFractionDigits: 2
				}).format(amount)}`;
			} else if (display === 'name') {
				const currencyNames = {
					USD: 'US Dollar', EUR: 'Euro', GBP: 'British Pound', 
					INR: 'Indian Rupee', CAD: 'Canadian Dollar', AUD: 'Australian Dollar'
				};
				formattedAmount = `${new Intl.NumberFormat(locale, { 
					minimumFractionDigits: 2,
					maximumFractionDigits: 2
				}).format(amount)} ${currencyNames[currency] || currency}`;
			} else {
				// Fallback to symbol display
				formattedAmount = new Intl.NumberFormat(locale, { 
					style: 'currency', 
					currency,
					currencyDisplay: 'symbol'
				}).format(amount);
			}
		}

		// Truncate if still too long
		if (formattedAmount.length > maxLength) {
			return formattedAmount.substring(0, maxLength - 1) + '…';
		}

		return formattedAmount;
	}, [preferences.currency, preferences.currencyDisplay, user?.currency]);
	
	const formatDate = (dateString) => {
		if (!dateString || dateString === '' || dateString === 'Not specified') return 'Not specified';
		try {
			let date;
			
			// Handle DD/MM/YYYY format
			if (typeof dateString === 'string' && dateString.includes('/')) {
				const [day, month, year] = dateString.split('/');
				if (year && month && day && year.length === 4) {
					date = new Date(year, month - 1, day);
				}
			}
			// Handle YYYY-MM-DD format
			else if (typeof dateString === 'string' && dateString.includes('-') && dateString.length === 10) {
				date = new Date(dateString);
			}
			// Try to parse as regular date
			else {
				date = new Date(dateString);
			}
			
			if (!date || isNaN(date.getTime())) return 'Not specified';
			return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
		} catch (error) {
			console.warn('Date formatting error:', error);
			return 'Not specified';
		}
	};

	const updatePreferences = async (patch) => {
		setPreferences(p => ({ ...p, ...patch }));
		if (isAuthenticated && token) {
			try {
				await api.updatePreferences(patch, token);
			} catch (err) {
				console.error('Failed to update preferences:', err);
			}
		}
	};

	const updateCategories = async (newCategories) => {
		setCategories(newCategories);
		if (isAuthenticated && token) {
			try {
				await api.updateCategories(newCategories, token);
			} catch (err) {
				console.error('Failed to update categories:', err);
			}
		}
	};

	const changePassword = async (currentPassword, newPassword) => {
		if (isAuthenticated && token) {
			try {
				const response = await api.post('/auth/change-password', { 
					currentPassword, 
					newPassword 
				}, { token });
				return { success: true, message: response.message };
			} catch (err) {
				console.error('Failed to change password:', err);
				return { success: false, error: err.message };
			}
		}
		return { success: false, error: 'Not authenticated' };
	};

	const clearData = async (password) => {
		if (isAuthenticated && token) {
			try {
				const response = await request('/auth/clear-data', { 
					method: 'DELETE',
					body: { password },
					token
				});
				// Reset local state
				setCategories({
					income: ['Salary', 'Business', 'Freelance', 'Investment', 'Other Income'],
					expense: ['Food & Dining', 'Transportation', 'Shopping', 'Entertainment', 'Bills & Utilities', 'Healthcare', 'Education', 'Travel', 'Other Expense']
				});
				return { success: true, message: response.message };
			} catch (err) {
				console.error('Failed to clear data:', err);
				return { success: false, error: err.message };
			}
		}
		return { success: false, error: 'Not authenticated' };
	};

	const deleteAccount = async (password) => {
		if (isAuthenticated && token) {
			try {
				const response = await request('/auth/delete-account', { 
					method: 'DELETE',
					body: { password },
					token
				});
				// This will be handled by logout after successful deletion
				return { success: true, message: response.message };
			} catch (err) {
				console.error('Failed to delete account:', err);
				return { success: false, error: err.message };
			}
		}
		return { success: false, error: 'Not authenticated' };
	};

	return (
		<SettingsContext.Provider value={{ 
			preferences, 
			updatePreferences, 
			categories, 
			updateCategories, 
			formatCurrency, 
			formatDate,
			changePassword,
			clearData,
			deleteAccount,
			loading: false
		}}>
			{children}
		</SettingsContext.Provider>
	);
};

export default SettingsContext;
